import { Drawer, Flex } from '@mantine/core';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { PermissionRole } from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { InviteTeamMemberForm } from '@dealstack/frontend/shared/components/InviteTeamMemberForm/InviteTeamMemberForm';
import { useState } from 'react';

interface Props {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	permissions: {
		label: string;
		value: PermissionRole;
	}[];
	onInvite: (
		input: {
			firstName: string;
			lastName: string;
			email: string;
			role: PermissionRole;
			shouldCloseOnSuccess?: boolean;
		},
		params?: {
			onSuccess: (refetchFn?: () => void) => void;
		}
	) => void;
	isLoading: boolean;
	description: string;
	roleLabel: string;
	submitLabel?: string;
	submitAndAddAnotherLabel?: string;
}

export const InviteTeamMemberDrawer = ({
	isOpen,
	setIsOpen,
	onInvite,
	permissions,
	description,
	roleLabel,
	isLoading,
	submitLabel,
	submitAndAddAnotherLabel,
}: Props) => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const [shouldResetForm, setShouldResetForm] = useState(false);

	const handleInviteMember = (
		firstName: string,
		lastName: string,
		email: string,
		role: PermissionRole,
		shouldCloseOnSuccess?: boolean
	) => {
		onInvite(
			{
				firstName,
				lastName,
				email,
				role,
			},
			{
				onSuccess: (refetchFn) => {
					showToastV2({
						message: tAdminDashboard('invite_team_member.toast.success_message'),
					});

					refetchFn?.();
					setShouldResetForm(true);

					if (shouldCloseOnSuccess) {
						setIsOpen(false);
					}
				},
			}
		);
	};

	const handleCloseClicked = () => {
		setShouldResetForm(true);
		setIsOpen(false);
	};

	return (
		<Drawer
			opened={isOpen}
			onClose={handleCloseClicked}
			position="right"
			transitionProps={drawerTransitionProps}
			sx={() => ({
				'.mantine-Drawer-header': {
					alignItems: 'flex-start',
				},
			})}
			trapFocus={false}
			title={
				<Flex direction="column" gap="0.5rem">
					<Text variant="h4" semiBold elipsis>
						{tAdminDashboard('invite_member_drawer.title')}
					</Text>
					<Text variant="p-md">{description}</Text>
				</Flex>
			}
		>
			{isLoading && <InlineLoader />}
			<InviteTeamMemberForm
				permissions={permissions}
				roleLabel={roleLabel}
				handleInviteMember={(firstName, lastName, email, role, shouldCloseOnSuccess) =>
					handleInviteMember(firstName, lastName, email, role, shouldCloseOnSuccess)
				}
				shouldResetForm={shouldResetForm}
				submitLabel={submitLabel}
				submitAndAddAnotherLabel={submitAndAddAnotherLabel}
			/>
		</Drawer>
	);
};
